import React from 'react';
import PropTypes from 'prop-types';
import Breadcrumbs from '../../Breadcrumbs';
import './SecondaryBannerV2.css';

/**
 * SecondaryBannerV2 — the editorial banner that opens a section: breadcrumb,
 * H1 title and copy, centred, on a gradient, a colour or a photo.
 *
 * Figma: OVER-LL `2699:15510` (desktop 1600×208). There is no mobile frame —
 * mobile follows the tokens (H1 / spacing / layout padding switch at 1024px).
 *
 * - Without a `title` the block is not rendered; the breadcrumb and the copy
 *   appear when their data is passed.
 * - Surface: the `--secondary-banner-v2-bg` component token (the Figma
 *   gradient, built from brand grays) unless `background` passes any CSS
 *   colour / gradient. `backgroundImage` lays a photo over that surface (the
 *   surface shows while it loads); `backgroundImageMobile` falls back to it.
 * - `theme="dark"` turns the text light for a dark photo or colour.
 */
const SecondaryBannerV2 = ({
  title,
  copy,
  breadcrumbs = [],
  breadcrumbsLabel,
  background,
  backgroundImage,
  backgroundImageMobile,
  theme = 'light',
  className = '',
  style,
  ...rest
}) => {
  if (!title || !String(title).trim()) return null;

  const classes = [
    'secondary-banner-v2',
    theme === 'dark' && 'secondary-banner-v2--dark',
    className,
  ].filter(Boolean).join(' ');

  return (
    <section
      className={classes}
      style={background ? { ...style, '--secondary-banner-v2-bg': background } : style}
      {...rest}
    >
      {backgroundImage && (
        <picture className="secondary-banner-v2__media" aria-hidden="true">
          <source media="(min-width: 1024px)" srcSet={backgroundImage} />
          <img className="secondary-banner-v2__image" src={backgroundImageMobile || backgroundImage} alt="" />
        </picture>
      )}

      <div className="secondary-banner-v2__container">
        <div className="secondary-banner-v2__content">
          <div className="secondary-banner-v2__top">
            {breadcrumbs?.length > 0 && (
              <Breadcrumbs
                items={breadcrumbs}
                ariaLabel={breadcrumbsLabel}
                className="secondary-banner-v2__breadcrumbs"
              />
            )}
            <h1 className="typo-h1 secondary-banner-v2__title">{title}</h1>
          </div>
          {copy && <p className="typo-body secondary-banner-v2__copy">{copy}</p>}
        </div>
      </div>
    </section>
  );
};

SecondaryBannerV2.propTypes = {
  /** Page title (Title/H1). Empty → the block is not rendered */
  title: PropTypes.string,
  /** Description under the title (P.Regular) */
  copy: PropTypes.node,
  /** Breadcrumb trail `[{ label, href?, onClick? }]` — see Breadcrumbs; empty → hidden */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  /** Accessible name of the breadcrumb nav (Breadcrumbs `ariaLabel`) */
  breadcrumbsLabel: PropTypes.string,
  /** Surface: any CSS colour or gradient; default = the `--secondary-banner-v2-bg` token */
  background: PropTypes.string,
  /** Photo over the surface (desktop; also mobile unless backgroundImageMobile is set) */
  backgroundImage: PropTypes.string,
  /** Mobile photo (< 1024px) */
  backgroundImageMobile: PropTypes.string,
  /** Text colours: light surface (dark text) or dark surface / photo (light text) */
  theme: PropTypes.oneOf(['light', 'dark']),
  /** Additional CSS class on the section */
  className: PropTypes.string,
  /** Inline style on the section (merged with `background`) */
  style: PropTypes.object,
};

export default SecondaryBannerV2;
